import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/apiConfig';
import Footer from './Footer';

const API = getApiBaseUrl();

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [moreUser, setMoreUser] = useState(null); // 选中的用户
  const [confirmText, setConfirmText] = useState('');

  // 积分管理相关状态
  const [userPoints, setUserPoints] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // 提交限制管理相关状态
  const [submissionLimitInfo, setSubmissionLimitInfo] = useState(null);
  const [submissionLimitLoading, setSubmissionLimitLoading] = useState(false);
  const [newDailyLimit, setNewDailyLimit] = useState('');
  const [limitExpiresHours, setLimitExpiresHours] = useState('24');
  const [limitReason, setLimitReason] = useState('');
  const [adjustingLimit, setAdjustingLimit] = useState(false);

  // 用 ref 保存查询词，使得 fetchUsers 的引用稳定，同时能读取最新查询值
  const qRef = useRef(q);
  useEffect(() => { qRef.current = q; }, [q]);

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const query = qRef.current ? `?q=${encodeURIComponent(qRef.current)}` : '';
      const res = await fetch(`${API}/auth/admin/users${query}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '获取用户失败');
      setUsers(data);
    } catch (e) {
      setError(e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取用户积分
  const fetchUserPoints = useCallback(async (userId) => {
    setPointsLoading(true);
    try {
      const res = await fetch(`${API}/points/admin/wallet/${userId}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '获取积分失败');
      setUserPoints(data);
    } catch (e) {
      alert(`获取积分失败: ${e.message}`);
    } finally {
      setPointsLoading(false);
    }
  }, []);

  // 调整用户积分
  const handleAdjustPoints = async () => {
    if (!adjustAmount || !adjustReason) {
      alert('请填写调整金额和原因');
      return;
    }

    const amount = parseInt(adjustAmount);
    if (isNaN(amount) || amount === 0) {
      alert('请输入有效的调整金额（不能为0）');
      return;
    }

    setAdjusting(true);
    try {
      const res = await fetch(`${API}/points/admin/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_id: moreUser._id,
          amount: amount,
          reason: adjustReason
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '调整失败');

      // 更新积分显示
      setUserPoints(data);

      // 提示成功
      const operation = amount > 0 ? '增加' : '减少';
      alert(`✅ 成功${operation}${Math.abs(amount)}积分`);

      // 重置表单
      setAdjustAmount('');
      setAdjustReason('');

      // ✅ 修复：关闭对话框
      setMoreUser(null);
    } catch (e) {
      alert(`调整失败: ${e.message}`);
    } finally {
      setAdjusting(false);
    }
  };

  // 获取用户提交限制信息
  const fetchSubmissionLimitInfo = useCallback(async (userId) => {
    setSubmissionLimitLoading(true);
    try {
      // 修复：使用管理员端点获取指定用户的提交限制信息
      const res = await fetch(`${API}/auth/admin/users/${userId}/submission-limit`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '获取提交限制失败');
      setSubmissionLimitInfo(data);
    } catch (e) {
      alert(`获取提交限制失败: ${e.message}`);
    } finally {
      setSubmissionLimitLoading(false);
    }
  }, []);

  // 调整用户提交限制
  const handleAdjustSubmissionLimit = async () => {
    if (!newDailyLimit) {
      alert('请输入新的每日限制');
      return;
    }

    const limit = parseInt(newDailyLimit);
    if (isNaN(limit) || limit < 1 || limit > 100) {
      alert('每日限制必须在1-100之间');
      return;
    }

    const hours = parseInt(limitExpiresHours);
    if (isNaN(hours) || hours < 1 || hours > 720) {
      alert('有效期必须在1-720小时之间');
      return;
    }

    setAdjustingLimit(true);
    try {
      const res = await fetch(`${API}/auth/admin/users/${moreUser._id}/submission-limit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          daily_limit: limit,
          expires_hours: hours,
          reason: limitReason
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '调整失败');

      // 提示成功
      alert(`✅ 成功调整用户${moreUser.username}的提交限制为${limit}次/天，有效期${hours}小时`);

      // 重置表单
      setNewDailyLimit('');
      setLimitExpiresHours('24');
      setLimitReason('');

      // 关闭对话框
      setMoreUser(null);

      // 重新加载用户列表
      fetchUsers();
    } catch (e) {
      alert(`调整失败: ${e.message}`);
    } finally {
      setAdjustingLimit(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  return (
    <>
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/home')}
            className="inline-flex items-center px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回个人中心
          </button>
          <h1 className="text-2xl font-bold text-gray-800">管理后台（用户列表）</h1>
        </div>
        <div className="flex items-center space-x-2">
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="搜索用户名/手机号" className="border rounded px-3 py-2 text-sm" />
          <button onClick={fetchUsers} className="px-3 py-2 bg-blue-600 text-white rounded">搜索/刷新</button>
        </div>
      </div>
        {loading && <div className="p-4 bg-white rounded shadow">加载中...</div>}
        {error && <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded shadow">{error}</div>}
        {!loading && !error && (
          <div className="bg-white rounded shadow overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100 text-left text-sm">
                  <th className="px-4 py-2">用户名</th>
                  <th className="px-4 py-2">手机号</th>
                  <th className="px-4 py-2">状态</th>
                  <th className="px-4 py-2">积分</th>
                  <th className="px-4 py-2">总提交</th>
                  <th className="px-4 py-2">成功</th>
                  <th className="px-4 py-2">创建时间</th>
                  <th className="px-4 py-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} className="border-t text-sm">
                    <td className="px-4 py-2">{u.username}</td>
                    <td className="px-4 py-2">{u.phone || '-'}</td>
                    <td className="px-4 py-2">{u.is_active ? '启用' : '停用'}</td>
                    <td className="px-4 py-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">{u.points_balance || 0}</span>
                    </td>
                    <td className="px-4 py-2 text-center">{u.total_submissions || 0}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`${u.successful_submissions > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'} px-2 py-1 rounded`}>
                        {u.successful_submissions || 0}
                      </span>
                    </td>
                    <td className="px-4 py-2">{new Date(u.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 space-x-2">
                      <button
                        onClick={async ()=>{
                          try{
                            const res = await fetch(`${API}/auth/admin/users/${u._id}`, {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({ is_active: !u.is_active })
                            });
                            const data = await res.json();
                            if(!res.ok) throw new Error(data.detail || '更新失败');
                            setUsers(prev => prev.map(x => x._id === u._id ? data : x));
                          }catch(e){
                            alert(e.message || '更新失败');
                          }
                        }}
                        className={`px-3 py-1 rounded text-white ${u.is_active ? 'bg-red-600' : 'bg-green-600'}`}
                      >{u.is_active ? '停用' : '启用'}</button>

                      <button
                        onClick={() => {
                          setMoreUser(u);
                          setConfirmText('');
                          setUserPoints(null);
                          setAdjustAmount('');
                          setAdjustReason('');
                          setSubmissionLimitInfo(null);
                          setNewDailyLimit('');
                          setLimitExpiresHours('24');
                          setLimitReason('');
                          // 自动获取用户积分
                          fetchUserPoints(u._id);
                          // 自动获取用户提交限制信息
                          fetchSubmissionLimitInfo(u._id);
                        }}
                        className="px-3 py-1 rounded text-white bg-gray-700"
                      >更多</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

    {/* 更多操作对话框 */}
    {moreUser && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 overflow-y-auto">
        <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6 my-8">
          <div className="text-lg font-semibold mb-3">对用户进行操作</div>
          <div className="text-sm text-gray-600 mb-4">
            用户名：<span className="font-mono">{moreUser.username}</span><br/>
            用户ID：<span className="font-mono">{moreUser._id}</span>
          </div>

          <div className="space-y-4">
            {/* 积分管理区域 */}
            <div className="border-t pt-4">
              <div className="text-base font-semibold mb-3 text-indigo-700">💰 积分管理</div>

              {pointsLoading ? (
                <div className="p-3 bg-gray-50 rounded text-sm">加载积分中...</div>
              ) : userPoints ? (
                <div className="space-y-3">
                  <div className="p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded">
                    <div className="text-2xl font-bold text-indigo-700">{userPoints.balance}</div>
                    <div className="text-sm text-gray-600">当前积分余额</div>
                    <div className="text-xs text-gray-500 mt-1">
                      最后更新: {new Date(userPoints.updated_at).toLocaleString()}
                    </div>
                  </div>

                  <div className="border rounded p-3 bg-gray-50">
                    <div className="text-sm font-semibold mb-2">调整积分</div>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">调整金额（正数加分，负数减分）</label>
                        <input
                          type="number"
                          value={adjustAmount}
                          onChange={e => setAdjustAmount(e.target.value)}
                          placeholder="如：100 或 -50"
                          className="w-full border rounded px-3 py-2 text-sm"
                          disabled={adjusting}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">调整原因</label>
                        <input
                          type="text"
                          value={adjustReason}
                          onChange={e => setAdjustReason(e.target.value)}
                          placeholder="如：活动奖励 或 违规扣分"
                          className="w-full border rounded px-3 py-2 text-sm"
                          disabled={adjusting}
                        />
                      </div>
                      <button
                        onClick={handleAdjustPoints}
                        disabled={adjusting || !adjustAmount || !adjustReason}
                        className={`w-full px-4 py-2 rounded text-white ${
                          adjusting || !adjustAmount || !adjustReason
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                      >
                        {adjusting ? '调整中...' : '确认调整'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-red-50 text-red-600 rounded text-sm">无法获取积分信息</div>
              )}
            </div>

            {/* 提交限制管理区域 */}
            <div className="border-t pt-4">
              <div className="text-base font-semibold mb-3 text-green-700">📋 提交限制管理</div>

              {submissionLimitLoading ? (
                <div className="p-3 bg-gray-50 rounded text-sm">加载提交限制中...</div>
              ) : submissionLimitInfo ? (
                <div className="space-y-3">
                  <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-sm font-semibold text-green-700">{submissionLimitInfo.daily_limit}</div>
                        <div className="text-xs text-gray-600">每日限制(次)</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-blue-700">{submissionLimitInfo.remaining}</div>
                        <div className="text-xs text-gray-600">剩余次数</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-orange-700">{submissionLimitInfo.used_today}</div>
                        <div className="text-xs text-gray-600">今日已用</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-purple-700">{new Date(submissionLimitInfo.reset_time).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-600">重置时间</div>
                      </div>
                    </div>
                    {submissionLimitInfo.is_temporary_limit && (
                      <div className="text-xs text-red-600 mt-2">
                        ⚠️ 临时限制，过期时间: {new Date(submissionLimitInfo.temporary_expires).toLocaleString()}
                      </div>
                    )}
                  </div>

                  <div className="border rounded p-3 bg-gray-50">
                    <div className="text-sm font-semibold mb-2">调整限制</div>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">新的每日限制（1-100）</label>
                        <input
                          type="number"
                          value={newDailyLimit}
                          onChange={e => setNewDailyLimit(e.target.value)}
                          placeholder="如：10"
                          min="1"
                          max="100"
                          className="w-full border rounded px-3 py-2 text-sm"
                          disabled={adjustingLimit}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">有效期（小时，1-720）</label>
                        <input
                          type="number"
                          value={limitExpiresHours}
                          onChange={e => setLimitExpiresHours(e.target.value)}
                          placeholder="如：24"
                          min="1"
                          max="720"
                          className="w-full border rounded px-3 py-2 text-sm"
                          disabled={adjustingLimit}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">调整原因（可选）</label>
                        <input
                          type="text"
                          value={limitReason}
                          onChange={e => setLimitReason(e.target.value)}
                          placeholder="如：特殊需求 或 违规处理"
                          className="w-full border rounded px-3 py-2 text-sm"
                          disabled={adjustingLimit}
                        />
                      </div>
                      <button
                        onClick={handleAdjustSubmissionLimit}
                        disabled={adjustingLimit || !newDailyLimit}
                        className={`w-full px-4 py-2 rounded text-white ${
                          adjustingLimit || !newDailyLimit
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {adjustingLimit ? '调整中...' : '确认调整'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-red-50 text-red-600 rounded text-sm">无法获取提交限制信息</div>
              )}
            </div>

            {/* 账户管理区域 */}
            <div className="border-t pt-4">
              <div className="text-base font-semibold mb-3 text-orange-700">🔐 账户管理</div>
              <button
                className="w-full px-4 py-2 rounded bg-yellow-600 text-white hover:bg-yellow-700"
                onClick={async ()=>{
                  try{
                    const res = await fetch(`${API}/auth/admin/users/${moreUser._id}`, {
                      method: 'DELETE',
                      credentials: 'include'
                    });
                    const data = await res.json();
                    if(!res.ok) throw new Error(data.detail || '删除失败');
                    setUsers(prev => prev.filter(x => x._id !== moreUser._id));
                    setMoreUser(null);
                  }catch(e){ alert(e.message || '删除失败'); }
                }}
              >软删除（标记停用）</button>

              <div className="border rounded p-3 mt-3">
                <div className="text-sm text-red-700 mb-2">危险操作：硬删除（不可恢复）。输入用户名 <b>{moreUser.username}</b> 确认。</div>
                <input
                  value={confirmText}
                  onChange={e=>setConfirmText(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm mb-2"
                  placeholder={`请输入 ${moreUser.username} 或 DELETE`}
                />
                <button
                  disabled={!(confirmText === moreUser.username || confirmText === 'DELETE')}
                  className={`w-full px-4 py-2 rounded ${ (confirmText === moreUser.username || confirmText === 'DELETE') ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                  onClick={async ()=>{
                    try{
                      const res = await fetch(`${API}/auth/admin/users/${moreUser._id}?hard=true`, { method: 'DELETE', credentials: 'include' });
                      const data = await res.json();
                      if(!res.ok) throw new Error(data.detail || '硬删除失败');
                      setUsers(prev => prev.filter(x => x._id !== moreUser._id));
                      setMoreUser(null);
                    }catch(e){ alert(e.message || '硬删除失败'); }
                  }}
                >硬删除（不可恢复）</button>
              </div>
            </div>

            <button className="w-full px-4 py-2 rounded bg-gray-100 hover:bg-gray-200" onClick={()=>setMoreUser(null)}>取消</button>
          </div>
        </div>
      </div>
    )}
      <Footer variant="simple" showCopyright={false} />
  </>
  );
};

export default AdminUsers;
